import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class GsapScrollUtils {
  constructor({ autoRefreshOnResize = false, resizeDebounce = 150 } = {}) {
    this.gsap = gsap;
    this.ScrollTrigger = ScrollTrigger;

    this._resizeDebounce = resizeDebounce;
    this._resizeTimeout = null;
    this._boundResizeHandler = null;

    if (autoRefreshOnResize) this.enableResizeRefresh();
  }

  createPin(options = {}) {
    const {
      trigger,
      start = "top top",
      end = "+=100%",
      pin = true,
      endTrigger = undefined,
      pinSpacing = true,
      markers = false,
      scroller = undefined,
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      playOnEnterBack = undefined,
    } = options;

    const scrollerEl =
      typeof scroller === "undefined" &&
      typeof window !== "undefined" &&
      window.__lenis_scroller
        ? window.__lenis_scroller
        : scroller;

    const st = {
      trigger,
      start,
      end,
      pin,
      pinSpacing,
      markers,
      scroller: scrollerEl,
    };
    if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
    if (typeof onToggle !== "undefined") st.onToggle = onToggle;
    if (typeof once !== "undefined") st.once = once;
    if (toggleActions) {
      st.toggleActions = toggleActions;
    } else if (typeof playOnEnterBack !== "undefined") {
      if (playOnEnterBack === true) {
        // For non-scrubbed triggers we want the animation to restart when re-entering
        // Use 'restart' so the animation actually restarts from the beginning
        st.toggleActions = "play none none reverse";
      } else {
        st.toggleActions = "play none none none";
      }
    }

    return ScrollTrigger.create(st);
  }

  /**
   * Create a ScrollTrigger instance with common defaults and optional onUpdate handling.
   * Options: { trigger, start, end, scrub, pin, markers, scroller, endTrigger, once, toggleActions, onToggle, onUpdate }
   */
  createScrollTrigger(options = {}) {
    const {
      trigger,
      start = "top center",
      end = "bottom top",
      scrub = false,
      pin = false,
      markers = false,
      scroller = undefined,
      endTrigger = undefined,
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      onUpdate = undefined,
      playOnEnterBack = undefined,
    } = options;

    const scrollerEl =
      typeof scroller === "undefined" &&
      typeof window !== "undefined" &&
      window.__lenis_scroller
        ? window.__lenis_scroller
        : scroller;

    const st = {
      trigger,
      start,
      end,
      scrub,
      pin,
      markers,
      scroller: scrollerEl,
    };

    if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
    if (typeof onToggle !== "undefined") st.onToggle = onToggle;
    if (typeof once !== "undefined") st.once = once;
    if (toggleActions) {
      st.toggleActions = toggleActions;
    } else if (typeof playOnEnterBack !== "undefined") {
      if (playOnEnterBack === true) {
        if (!st.scrub) st.toggleActions = "play none none reverse";
      } else {
        st.toggleActions = "play none none none";
      }
    }

    if (typeof onUpdate !== "undefined") {
      if (typeof onUpdate === "function") {
        st.onUpdate = (self) => {
          try {
            onUpdate(self);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("GsapScrollUtils: onUpdate callback error", e);
          }
        };
      } else {
        // allow passing a named handler string (less common) or ignore
        st.onUpdate = onUpdate;
      }
    }

    return ScrollTrigger.create(st);
  }

  createScrubTimeline(options = {}) {
    const {
      trigger,
      start = "top top",
      end = "+=100%",
      scrub = true,
      endTrigger = undefined,
      pin = false,
      markers = false,
      scroller = undefined,
      defaults = {},
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      playOnEnterBack = undefined,
    } = options;

    const scrollerEl =
      typeof scroller === "undefined" &&
      typeof window !== "undefined" &&
      window.__lenis_scroller
        ? window.__lenis_scroller
        : scroller;

    const st = {
      trigger,
      start,
      end,
      scrub,
      pin,
      markers,
      scroller: scrollerEl,
    };
    if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
    if (typeof onToggle !== "undefined") st.onToggle = onToggle;
    if (typeof once !== "undefined") st.once = once;
    if (toggleActions) {
      st.toggleActions = toggleActions;
    } else if (typeof playOnEnterBack !== "undefined") {
      if (playOnEnterBack === true) {
        // Only set toggleActions to restart on enter-back when the timeline is NOT scrubbed
        if (!st.scrub) st.toggleActions = "play none none reverse";
      } else {
        st.toggleActions = "play none none none";
      }
    }

    return gsap.timeline({
      defaults,
      scrollTrigger: st,
    });
  }

  /**
   * Create a GSAP timeline with optional ScrollTrigger configuration.
   * If `trigger` is provided the timeline will be bound to a ScrollTrigger,
   * otherwise a normal timeline is returned.
   *
   * Options: { defaults, trigger, start, end, scrub, pin, markers, scroller, once, toggleActions, playOnEnterBack }
   */
  createTimeline(options = {}) {
    const {
      defaults = {},
      trigger = null,
      start = "top center",
      end = "bottom top",
      scrub = false,
      endTrigger = undefined,
      pin = false,
      markers = false,
      scroller = undefined,
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      playOnEnterBack = undefined,
    } = options;

    if (trigger) {
      const scrollerEl =
        typeof scroller === "undefined" &&
        typeof window !== "undefined" &&
        window.__lenis_scroller
          ? window.__lenis_scroller
          : scroller;

      const st = {
        trigger,
        start,
        end,
        scrub,
        pin,
        markers,
        scroller: scrollerEl,
      };
      if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
      if (typeof onToggle !== "undefined") st.onToggle = onToggle;
      if (typeof once !== "undefined") st.once = once;
      if (toggleActions) {
        st.toggleActions = toggleActions;
      } else if (typeof playOnEnterBack !== "undefined") {
        if (playOnEnterBack === true) {
          if (!st.scrub) st.toggleActions = "play none none reverse";
        } else {
          st.toggleActions = "play none none none";
        }
      }

      return gsap.timeline({
        defaults,
        scrollTrigger: st,
      });
    }

    return gsap.timeline({ defaults });
  }

  toWithScroll(target, toVars = {}, options = {}) {
    const {
      trigger = null,
      start = "top center",
      end = "bottom top",
      scrub = false,
      endTrigger = undefined,
      markers = false,
      scroller = undefined,
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      playOnEnterBack = undefined,
    } = options;

    if (trigger) {
      const scrollerEl =
        typeof scroller === "undefined" &&
        typeof window !== "undefined" &&
        window.__lenis_scroller
          ? window.__lenis_scroller
          : scroller;
      const st = { trigger, start, end, scrub, markers, scroller: scrollerEl };
      if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
      if (typeof onToggle !== "undefined") st.onToggle = onToggle;
      if (typeof once !== "undefined") st.once = once;
      if (toggleActions) {
        st.toggleActions = toggleActions;
      } else if (typeof playOnEnterBack !== "undefined") {
        if (playOnEnterBack === true) {
          if (!st.scrub) st.toggleActions = "play none none reverse";
        } else {
          st.toggleActions = "play none none none";
        }
      }

      const tl = gsap.timeline({ scrollTrigger: st });

      tl.to(target, toVars);
      return tl;
    }

    return gsap.to(target, toVars);
  }

  fromToWithScroll(target, fromVars = {}, toVars = {}, options = {}) {
    const {
      trigger = null,
      start = "top center",
      end = "bottom top",
      scrub = false,
      endTrigger = undefined,
      markers = false,
      scroller = undefined,
      once = undefined,
      toggleActions = undefined,
      onToggle = undefined,
      onUpdate = undefined,
      playOnEnterBack = undefined,
    } = options;

    if (trigger) {
      const scrollerEl =
        typeof scroller === "undefined" &&
        typeof window !== "undefined" &&
        window.__lenis_scroller
          ? window.__lenis_scroller
          : scroller;
      const st = { trigger, start, end, scrub, markers, scroller: scrollerEl };
      if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
      if (typeof onToggle !== "undefined") st.onToggle = onToggle;
      if (typeof onUpdate !== "undefined") st.onUpdate = onUpdate;
      if (typeof once !== "undefined") st.once = once;
      if (toggleActions) {
        st.toggleActions = toggleActions;
      } else if (typeof playOnEnterBack !== "undefined") {
        if (playOnEnterBack === true) {
          if (!st.scrub) st.toggleActions = "play none none reverse";
        } else {
          st.toggleActions = "play none none none";
        }
      }

      const tl = gsap.timeline({ scrollTrigger: st });

      tl.fromTo(target, fromVars, toVars);
      return tl;
    }

    return gsap.fromTo(target, fromVars, toVars);
  }

  fadeInOnEnter(selector, opts = {}) {
    const options = Object.assign(
      {
        stagger: 0.15,
        opacity: 0,
        duration: 0.8,
        ease: "power2.inOut",
        trigger: selector,
        start: "top 100%",
        end: "top 50%",
        scrub: true,
        markers: false,
      },
      opts,
    );

    const elements = document.querySelectorAll(selector);
    if (!elements || !elements.length) return null;

    elements.forEach((el) => {
      const tl = this.createScrubTimeline({
        trigger: elements.length > 1 ? el : options.trigger,
        start: options.start,
        end: options.end,
        scrub: options.scrub,
        markers: options.markers,
        once: options.once,
        toggleActions: options.toggleActions,
        playOnEnterBack: options.playOnEnterBack,
      });

      tl.fromTo(
        el,
        { opacity: options.opacity },
        {
          opacity: 1,
          stagger: options.stagger,
          duration: options.duration,
          ease: options.ease,
        },
      );

      // return tl;
    });
  }

  fadeOutOnLeave(selector, opts = {}) {
    const options = Object.assign(
      {
        stagger: 0.15,
        y: 0,
        opacity: 0,
        duration: 0.8,
        ease: "power2.inOut",
        trigger: selector,
        start: "top 20%",
        end: "top -20%",
        scrub: true,
        markers: false,
      },
      opts,
    );

    const elements = document.querySelectorAll(selector);
    if (!elements || !elements.length) return null;

    elements.forEach((el) => {
      const tl = this.createScrubTimeline({
        trigger: elements.length > 1 ? el : options.trigger,
        start: options.start,
        end: options.end,
        scrub: options.scrub,
        markers: options.markers,
        once: options.once,
        toggleActions: options.toggleActions,
        playOnEnterBack: options.playOnEnterBack,
      });

      tl.fromTo(
        el,
        { y: 0, opacity: 1 },
        {
          y: options.y,
          opacity: options.opacity,
          stagger: options.stagger,
          duration: options.duration,
          ease: options.ease,
        },
      );

      // return tl;
    });
  }

  to(target, vars = {}, options = {}) {
    const {
      trigger = null,
      start = "top center",
      end = "bottom top",
      scrub = false,
      endTrigger = undefined,
      markers = false,
      scroller = undefined,
    } = options;

    if (trigger) {
      const {
        once = undefined,
        toggleActions = undefined,
        playOnEnterBack = undefined,
        onToggle = undefined,
      } = options;

      const scrollerEl =
        typeof scroller === "undefined" &&
        typeof window !== "undefined" &&
        window.__lenis_scroller
          ? window.__lenis_scroller
          : scroller;

      const st = { trigger, start, end, scrub, markers, scroller: scrollerEl };
      if (typeof endTrigger !== "undefined") st.endTrigger = endTrigger;
      if (typeof onToggle !== "undefined") st.onToggle = onToggle;
      if (typeof once !== "undefined") st.once = once;
      if (toggleActions) {
        st.toggleActions = toggleActions;
      } else if (typeof playOnEnterBack !== "undefined") {
        if (playOnEnterBack === true) {
          // for simple 'to' tweens, respect playOnEnterBack when not scrubbed
          if (!st.scrub) st.toggleActions = "play none none reverse";
        } else {
          st.toggleActions = "play none none none";
        }
      }

      const tl = gsap.timeline({ scrollTrigger: st });

      tl.to(target, vars);
      return tl;
    }

    return gsap.to(target, vars);
  }

  killAll() {
    const all = ScrollTrigger.getAll();
    if (all && all.length) all.forEach((t) => t.kill());
  }

  handleResize(callback) {
    if (typeof callback === "function") {
      try {
        callback();
      } catch (e) {
        // swallow errors from user callback but continue with refresh
        // eslint-disable-next-line no-console
        console.warn("GsapScrollUtils: resize callback error", e);
      }
    }

    // Ask ScrollTrigger to recalculate positions and sizes
    if (
      this.ScrollTrigger &&
      typeof this.ScrollTrigger.refresh === "function"
    ) {
      this.ScrollTrigger.refresh();
    } else if (gsap && gsap.utils && gsap.utils.toArray) {
      // fallback: attempt a generic refresh by forcing a tick
      try {
        gsap.tick();
      } catch (e) {
        // noop
      }
    }
  }

  _debouncedResize(callback) {
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => {
      this.handleResize(callback);
      this._resizeTimeout = null;
    }, this._resizeDebounce);
  }

  enableResizeRefresh() {
    if (this._boundResizeHandler) return;
    this._boundResizeHandler = () => this._debouncedResize();
    window.addEventListener("resize", this._boundResizeHandler);
  }

  disableResizeRefresh() {
    if (!this._boundResizeHandler) return;
    window.removeEventListener("resize", this._boundResizeHandler);
    this._boundResizeHandler = null;
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }
  }
}
